import styled from '@emotion/styled';
import { RadioButton } from 'ui-components/common/RadioButton';
import { isPC } from 'constants/layout';
import { ChangeEvent, useCallback, useState } from 'react';
import { css } from '@emotion/react';

interface ContentSelectTypeProps<I extends string> {
  className?: string;
  id: string;
  list: I[];
  stringMap: Record<I, string>;
  defaultValue: I;
  disabled?: boolean;
  onChange?: (value: ChangeEvent<HTMLInputElement>) => void;
}

export const ContentSelectType = <I extends string>({
  className,
  id,
  list,
  stringMap,
  defaultValue,
  disabled = false,
  onChange,
}: ContentSelectTypeProps<I>) => {
  const [selected, setSelected] = useState(defaultValue);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (disabled) {
        return;
      }

      setSelected(e.target.value as I);
      onChange?.(e);
    },
    [disabled, onChange],
  );

  return (
    <StyledRadioWrapper className={className} disabled={disabled}>
      {list.map(it => (
        <RadioButton
          key={it}
          id={id}
          name={stringMap[it]}
          value={it}
          selectedValue={selected}
          onChange={handleChange}
        />
      ))}
    </StyledRadioWrapper>
  );
};

const StyledRadioWrapper = styled.div(
  ({ disabled }: { disabled: boolean }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;

    opacity: ${disabled ? 0.3 : 1};
    pointer-events: ${disabled ? 'none' : 'auto'};

    @media (${isPC}) {
      flex-direction: row;
      align-items: center;
      gap: 30px;
    }
  `,
);
